import MarkdownIt from "markdown-it";

/**
 * Markdown parser for agent-proposed `new_string` content. Configured to
 * match the parser TiptapEditor uses for incoming LLM content (GFM tables,
 * fenced code, html passthrough, linkify), so what the agent sees from
 * `read_open_document` round-trips cleanly through stage → accept.
 *
 * Kept as a module singleton — parsers are not cheap to construct and the
 * config is invariant.
 */
const md = new MarkdownIt({
    html: true,
    linkify: true,
    breaks: true
});
md.enable(["table", "fence"]);

/**
 * Heuristic: does this look like multi-block markdown that should be parsed,
 * rather than a plain-text inline replacement?
 *
 * Detects:
 *  • any line break (multi-paragraph or multi-line content)
 *  • leading block markdown markers — heading, list, blockquote, code fence
 *
 * Inline marks like **bold**, _italic_, [link](url) on a single line do NOT
 * count — those can be applied as a plain-text replacement, and the agent
 * shouldn't generally emit inline markdown syntax (the skill teaches it to
 * use plain text for anchors and replacements).
 */
export function isStructuralMarkdown(text: string): boolean {
    if (!text) return false;
    if (text.includes("\n")) return true;
    const trimmed = text.trimStart();
    return /^(#{1,6} |[-*+] |\d+\. |> |```)/.test(trimmed);
}

/**
 * Render `new_string` markdown to HTML. The result is fed to
 * `editor.commands.insertContent(html)` which uses tiptap's HTML parser
 * (configured by the editor's loaded extensions) to build a PM slice.
 */
export function renderMarkdownToHtml(markdown: string): string {
    // MarkdownIt with `breaks: true` turns every \n into <br>, which means
    // "item text\n3. next item" becomes a single <p> with a hard break rather
    // than a list. Normalise: a single newline directly before a list marker
    // (digit+period or bullet) becomes a blank line so MarkdownIt creates a
    // proper block-level list node instead.
    const normalised = markdown.replace(/\n([\d]+\. |-|\* |\+ )/g, '\n\n$1');
    return md.render(normalised);
}
